import React, { useEffect, useState, useRef, useCallback } from 'react'
import axios from 'axios';
import { IconButton, MenuItem, FormControl, TextField, InputLabel, Autocomplete, Select, Button } from '@mui/material';
import { DataGridPremium } from '@mui/x-data-grid-premium';
import { Close } from '@mui/icons-material';
import {
    Box,
    Typography
} from '@mui/material';
import { Search, ArrowDropDown } from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import { DeleteForeverRounded } from '@mui/icons-material';
import {
    TextAreaWithCount,
    TextFieldWithIconButton,
    ConfirmationDialog,
    NotificationDialog,
    UtilityLibrary,
    DebounceForm,
    ValidateForm,
    ApiManager,
    ScrapeDataManager,
    sessionService,
    SessionManager,
    PageLoader,
    DocumentUtility,
    LoggerUtility,
    EnumUtility,
    NarrativeModel,
    NarrativeBuilderModel,
    DeterminePermissions,
    CaseWorkflowService,
    ValidateRequiredFieldsInForm,
    SharedService} from '../components/ComponentsLibrary';
import { useConfigContext } from '../functions/ConfigContext';
import { ClientType } from '../enums/ClientType'
import { MasterEnum } from "../enums/MasterEnum";

const getMasterServerDBSchema = () => {
    const siteSettings = SessionManager.GetDataFromSession("SiteSettingsDetailsKey");
    const schema = siteSettings?.masterDBSchema;
    
    if (!schema) {
        console.warn('🔍 ManageKeywords: masterServerDBSchema not found in session');
        // Fallback: try to get from user session
        const userInfo = sessionService.getUser();
        return userInfo?.masterDBSchema || '';
    }
    
    return schema;
};

// Helper function to update form values
function updateArrayStateValues(arrayStateSetter, updatedValues) {
    try {
        arrayStateSetter((prevValues) => ({
            ...prevValues,
            ...updatedValues
        }));
    }
    catch (error) {
        throw LoggerUtility.ProcessErrorData(error, "updateArrayStateValues", {});
    }
};

function getParentIdByChildId(data, parentFieldName, childFieldName, childField, childId) {
    try {
        const parent = data.find((parent) =>
            parent[childFieldName].some((child) => child[childField] === childId)
        );
        return parent ? parent[parentFieldName] : 0; // Return 0 if no parent is found
    }
    catch (error) {
        throw LoggerUtility.ProcessErrorData(error, "getParentIdByChildId", {});
    }
};


function ManageKeywords({
    setter,
    caseBatchDocumentID = 0,
    showCasesByBatchDoc = false,
    resetExtCaseDetails = false,
    showAlertMessage = ('', ''),
}) {

    const { configLibrary } = useConfigContext();

    const errorFormName = "Manage Keywords";

    const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
    const [notificationDialogContent, setNotificationDialogContent] = useState([]);

    const masterServerDBSchema = SessionManager.GetDataFromSession("SiteSettingsDetailsKey")?.masterDBSchema;
    const stateID =  SessionManager.GetDataFromSession("LoginUserInfoKey")?.stateID;
    const timeZone = SessionManager.GetDataFromSession("LoginUserInfoKey")?.timeZone;

    const manageKeywordRef = useRef(null);
    const [caseText, setCaseText] = useState("");
    const [isSaveEnabled, setIsSaveEnabled] = useState(false);
    //let originalCaseList = [];
    const [originalCaseList, setOriginalCaseList] = useState([]);
    const [resultsCases, setCaseResults] = useState([]);// this is just for the case grid
    // const [caseDataArray, setCaseDataArray] = useState([]);
    const caseDataArrayRef = useRef([])
    const [paginationModel, setPaginationModel] = useState({ pageSize: 5, page: 0 });
    const fieldsForCaseList = ["batchDocumentID", "caseNumber", "cpName", "ncpName"]; // Fields required for list of cases
    const [isPageLoading, setIsPageLoading] = useState(false);
    const [loadingComplete, setLoadingComplete] = useState(false);
    const [removeCaseNumber, setRemoveCaseNumber] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [confirmationDialogContent, setConfirmationDialogContent] = useState([]);
    const [newCasesAdded, setNewCasesAdded] = useState(false);
    const [casesRemoved, setCasesRemoved] = useState(false);
    const [originalNarrative, setOriginalNarrative] = useState(null);
    const [originalActionNotes, setOriginalActionNotes] = useState(null);
    const [requiredFieldLabels, setRequiredFieldLabels] = useState({
        caseNumberTxt: "",
        noteForActionTxt: ""
    })
    const [allDocumentGroups, setAllDocumentGroups] = useState([]);
    const [withCaseGroupData, setWithCaseGroupData] = useState([]);
    const [withNoCaseGroupData, setWithNoCaseGroupData] = useState([]);

    const caseNumberRef = useRef();
    // Validation patterns
    const siteSettingDetail = JSON.parse(SessionManager.GetValueFromSession(SessionManager.SiteSettingsDetailsKey));
    const caseNumberRegex = siteSettingDetail.CaseNumberRegex || { caseRegexValue: "", caseRegexMessage: "" };
    //const caseNumberRegex = JSON.parse(SessionManager.GetValueFromSession(SessionManager.CaseNumberRegexKey)) || { caseRegexValue: "", caseRegexMessage: "" };

    const initialCaseNumberIconsDisabled = { search: false, copy: false, clear: false };
    const [caseNumberIconsDisabled, setCaseNumberIconsDisabled] = useState(initialCaseNumberIconsDisabled);
    const [disableKeyDownOnCaseSearch, setDisableKeyDownOnCaseSearch] = useState(false);
    // Track newly added cases that should be sent to iConnect
    const [newlyAddedCases, setNewlyAddedCases] = useState([]);

    // Narrative Character Limit
    const narrativeCharLimit = siteSettingDetail.NarrativeCharsLimit || 500;
    //const narrativeCharLimit = SessionManager.GetValueFromSession(SessionManager.NarrativeCharsLimitKey) || 500;

    // Notes Character Limit
    const notesCharLimit = siteSettingDetail.NotesCharsLimit || 500;
    //const notesCharLimit = SessionManager.GetValueFromSession(SessionManager.NotesCharsLimitKey) || 500;

    const [notesForAction, setNotesForAction] = useState("");
    const [narrativeState, setNarrativeState] = useState("");

    const noteForActionRef = useRef();
    const narrativeForStateRef = useRef();
    const [isConfidential, setIsConfidential] = useState(false)

    const [timeActionStart, setTimeActionStart] = useState(new Date().toISOString());
    const [dynamicDocGroupList, setDynamicDocGroupList] = useState([]);

    const defaultPermissionsForFields = {
        caseNumberSearchTxt: { read: false, write: true, hide: false },
        docGroupSelect: { read: false, write: true, hide: false },
        docTypeSelect: { read: false, write: true, hide: false },
        noteForActionTxt: { read: false, write: true, hide: false },
        narrativeForStateTxt: { read: false, write: true, hide: false },
        casesListDgrd: { read: false, write: true, hide: false },
        cancelBtn: { read: false, write: true, hide: false },
        saveBtn: { read: false, write: true, hide: false },
    };
    const [formFieldAccess, setFormFieldAccess] = useState(defaultPermissionsForFields);

    /**************************************************************************** */
    //Document Group dropdown API code
    const [docgrouplists, setDocgrouplists] = useState([]);
    const [selectedDocGroup, setSelectedDocGroup] = useState("");
    const [selectedGroupID, setSelectedGroupID] = useState('');

    //Document Type dropdown API code
    let originalDocType = "";
    const [selectedDocType, setSelectedDocType] = useState("");
    const [documentTypes, setDocumentTypes] = useState([]);


    const [documentData, setDocumentData] = useState(null);
    // Initial state for Batch Document Data
    const initialDocumentData = {
        documentStatusID: 0,
        batchID: 0,
        batchName: "",
        batchDocumentID: 0,
        documentGroupTypeID: 0,
        documentTypeID: 0,
        documentName: "",
        documentLocation: "",
        documentSourceTypeID: 0,
        lastIndexTS: null,
        docPageCount: 0,
        scanTS: null,
        indexedBy: "",
        activityStartTS: null,
        createdTS: null
    };

    // To-Do: Modify Code - Check these State variables
    const [caseByBatchDocData, setCaseByBatchDocData] = useState([]);

    
    const [initialFormValues, setInitialFormValues] = useState({
        caseNumberTxt: "",
        yTracCaseID: 0,
        caseNumberSearched: "",
        prevCaseChk: false,
        docGroupSelect: { id: 0, name: "All" },
        docTypeSelect: { id: -123456, name: "" },
        noteForActionTxt: "",
        narrativeForStateTxt: ""
        });
    
    const [formValues, setFormValues] = useState(initialFormValues);
    

    const emptyCaseData = {
        caseNumber: '',
        caseType: null,
        caseTypeID: null,
        CaseWorker: null,
        countyID: null,
        countyName: null,
        cpFirstName: null,
        cpFullName: null,
        cpLastName: null,
        createdTS: '',
        districtID: null,
        familyViolence: null,
        fkM_UserLoginID: 0,
        ncpFirstName: null,
        ncpFullName: null,
        ncpLastName: null,
        specialCircumstanceContent: null,
        specialCircumstanceContentActive: 0,
        statusType: null,
        statusTypeID: 0,
        yTracCaseID: 0,
    };
     const caseInfoMapping = {
        
        caseNumber: ["CaseNumber", "caseNumber"],   
        caseType: ["CaseType", "caseType"],             
        caseTypeID: "",                 
        caseWorker: ["CaseworkerName", "caseWorker"],  
        countyID: ["CountyID", "countyID"],             
        countyName: "",
        cpFirstName: ["CPFirstName", "cpFirstName"],
        cpFullName: ["CPName", "cpFullName"],
        cpLastName: ["CPLastName", "cpLastName"],
        cPSSN: "CPSSN",
        createdTS: "",
        districtID: "DistrictID",
        familyViolence: "FamilyViolence",
        fVIAndConfidentialCase: "FVIAndConfidentialCase",
        confidentialCase: "ConfidentialCase",
        fKM_UserLoginID: "",
        ncpFirstName: ["NCPFirstName", "ncpFirstName"],
        ncpFullName: ["NCPName", "ncpFullName"],
        ncpLastName: ["NCPLastName", "ncpLastName"],
        restrictedStatusMessage: "RestrictedStatusMessage",
        specialCircumstanceContent: "",
        specialCircumstanceContentActive: "",
        statusType: ["CaseStatus", "statusType"],   // This looks like a problem.
        statusTypeID: "",
        yTracCaseID: "yTracCaseID",

    };


    const columns = [
        {
            field: 'caseNumber', headerName: 'Case Number', flex: 1, sortable: true,
            renderCell: (params) => {
                return (
                    <div className="d-flex justify-content-start align-items-center" style={{ cursor: "pointer" }}>
                        <DeleteForeverRounded index={params.row.caseNumber}
                            onClick={() => handleConfirmationDialog(params.row.caseNumber)}
                            color='ywMainBlue' />
                        <div style={{ paddingLeft: "10px" }}>
                            {params.row.caseNumber}
                        </div>
                    </div>
                );
            }
        },
        { field: 'cpName', headerName: 'CP', valueFormatter: (value) => value && toPascalCase(value), flex: 1, sortable: true },
        { field: 'ncpName', headerName: 'NCP', valueFormatter: (value) => value && toPascalCase(value), flex: 1, sortable: true },
    ];

    const handleCloseManageKeywordsClick = async () => {
        try {
            const originalGroupTypeID = originalCaseList.length > 0 ? originalCaseList[0]?.originalDocumentGroupTypeID : null;
            //console.log(originalGroupTypeID, formValues?.groupTypeSelect)
            const hasGroupTypeChanged = originalGroupTypeID !== null && originalGroupTypeID !== formValues?.docGroupSelect;    
            const originalDocTypeID = originalCaseList.length > 0 ? originalCaseList[0]?.originalDocumentTypeID : null;
            //console.log(originalDocTypeID, formValues?.docTypeSelect)
            const hasDocTypeChanged = originalDocTypeID !== null && originalDocTypeID !== formValues?.docTypeSelect; 
            const hasNarrativeChanged = originalNarrative !== null && originalNarrative != formValues?.narrativeForStateTxt;
            if (hasDocTypeChanged || hasGroupTypeChanged || hasNarrativeChanged || newCasesAdded || casesRemoved) {
                handleCancelConfirmationDialog();
            }
            else {
                console.log("NO CHNGES DETECTED")
                 setter({
                    showManageKeyword: false,
                    showMessage: false,
                    dialogContent: ""
                })
            }
        }
        catch (error) {
            await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleCloseManageKeywordsClick");
        }
    };

    // UseEffect to handle post-loading actions
    useEffect(() => {
        try {
            if (loadingComplete) {
                // Disable the Manage Keywords once loading is complete
                setter({
                    showManageKeyword: false,
                    showMessage: true,
                    dialogContent: EnumUtility.NotificationAlerts.ManageKeywordsSuccess
                })

                // Reset the loadingComplete flag
                setLoadingComplete(false);
            }
        }
        catch (error) {
            LoggerUtility.ManageErrorLogging(error, errorFormName, "loadingComplete");
        }
    }, [loadingComplete]);

    /* 
      * Authorization code starts here
      * The permission values for fields are received from Web API
      * The received data from Web API response is processed and stored in a state variable "formFieldAccess"
      */
    useEffect(() => {
        const fetchFormFieldPermissions = async () => {
            try {
                const loginUserInfo = sessionService.getUser();
                const loginStateID = loginUserInfo ? loginUserInfo.stateID : -123456;
                const params = {
                    clientID: loginStateID,
                    formID: 70      // To-do: Modify Code - Hard-coded value of FormID: 70
                }
                const controlSecurityListApiData = await ApiManager.FetchControlsPermissions(params);
                const actualPermissionsForFields = DeterminePermissions(controlSecurityListApiData);
                setFormFieldAccess({ ...defaultPermissionsForFields, ...actualPermissionsForFields });
            } catch (error) {
                throw LoggerUtility.ProcessErrorData(error, "fetchFormFieldPermissions", {});
            }
        };

        try {
            fetchFormFieldPermissions();
        }
        catch (error) {
            LoggerUtility.ManageErrorLogging(error, errorFormName, "fetchFormFieldPermissions - page load");
        }
    }, []);


    const debouncedValidation = useCallback(
        DebounceForm((currentDocType, currentCases, currentFormValues, currentConfidential) => {
            try {
                // --- Rule Implementation ---
                const hasCases = currentCases.length > 0;
                const hasDocType = currentDocType !== "" && currentDocType !== null && currentDocType !== undefined;
                const hasDocGroup = selectedGroupID !== "" && selectedGroupID !== null && selectedGroupID !== undefined;

               

                const originalDocTypeID = originalCaseList.length > 0 ? originalCaseList[0]?.originalDocumentTypeID : null;
                
                const hasDocTypeChanged = originalDocTypeID !== null && originalDocTypeID !== currentDocType;
                const hasNewCases = currentCases.some(c => !originalCaseList.some(orig => orig.caseNumber === c.caseNumber));
                const hasRemovedCases = originalCaseList.length > currentCases.length;

                // --- Primary Save Conditions (When SAVE should be ON) ---
                let shouldSaveBeEnabled = false;
                if (hasDocTypeChanged) shouldSaveBeEnabled = true; // Rule 1
                if (hasNewCases) shouldSaveBeEnabled = true;       // Rule 2
                if (hasRemovedCases) shouldSaveBeEnabled = true;   // Rule 3
                setNewCasesAdded(hasNewCases);
                setCasesRemoved(hasRemovedCases);

                 // If any of these are false, SAVE is always disabled.
                if (!hasCases || !hasDocType || !hasDocGroup) {
                    setIsSaveEnabled(false);
                    return;
                }

                // --- Secondary Invalidation (When SAVE should be turned OFF) ---
                // This rule only applies if the primary conditions are NOT met.
                if (!shouldSaveBeEnabled) {
                    // Rule: If no changes (no new/removed cases, same doc type), disable save.
                    const noChanges = !hasNewCases && !hasRemovedCases && !hasDocTypeChanged;
                    if (noChanges) {
                        shouldSaveBeEnabled = false;
                    }
                }
                
                setIsSaveEnabled(shouldSaveBeEnabled);

            } catch (error) {
                throw LoggerUtility.ProcessErrorData(error, "debouncedValidation", {});
            }
        }, 300),
        [originalCaseList, selectedGroupID, isConfidential] // Dependencies for the validation logic
    );

    // Function to merge and map objects by ID
    const mergeArrays = (array1, array2, mapping1, mapping2) => {
        try {
            const mergedArray = array1.map((item1) => {
                const item2 = array2.find((item) => item.caseNumber === item1.caseNumber) || {};
                const mappedItem1 = {};
                const mappedItem2 = {};

                // Map fields from the first array
                Object.keys(mapping1).forEach((key) => {
                    if (item1[key] !== undefined) {
                        mappedItem1[mapping1[key]] = item1[key];
                    }
                });

                // Map fields from the second array
                Object.keys(mapping2).forEach((key) => {
                    if (item2[key] !== undefined) {
                        mappedItem2[mapping2[key]] = item2[key];
                    }
                });

                // Combine mapped items and retain the ID
                return { caseNumber: item1.caseNumber, ...mappedItem1, ...mappedItem2 };
            });

            return mergedArray;
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "mergeArrays", {});
        }
    };

    //const mergedArray = mergeArrays(sourceArray1, sourceArray2, fieldMapping1, fieldMapping2);

    // Mapping for fields from both arrays
    const casefieldMapping1 = {
        yTracCaseID: "yTracCaseID",
        isNewlyAdded: "isNewlyAdded",
        crudTypeID: "crudTypeID",
        notes: "notes",
        narrative: "narrative",
        cpssn: "cpssn", 
        ncpssn: "ncpssn"
        };

    const casefieldMapping2 = {
        documentTypeID: "documentTypeID",
        documentGroupTypeID: "documentGroupTypeID",
        documentSource: "documentSourceTypeID"
    };


    useEffect(() => {
        const fetchDbOnlyData = async () => {
            try {
                await loadInitialDataDbOnly(caseBatchDocumentID);
            }
            catch (error) {
                throw LoggerUtility.ProcessErrorData(error, "fetchDbOnlyData", {});
            }
        };

        try {
            if (!showCasesByBatchDoc && caseBatchDocumentID) {
                setTimeActionStart(new Date().toISOString());
                fetchDbOnlyData();
            }
        }
        catch (error) {
            LoggerUtility.ManageErrorLogging(error, errorFormName, "fetchDbOnlyData - page load");
        }
    }, [caseBatchDocumentID, showCasesByBatchDoc]);

    // useEffect(() => {
    //     // 🔍 CONSOLIDATED: Handle all case transition scenarios in one place
    //     console.log('🔍 useEffect triggered:', {
    //         resultsCasesLength: resultsCases.length,
    //         originalCaseListLength: originalCaseList.length,
    //         selectedGroupID,
    //         selectedDocType
    //     });

    //     const currentDocGroupList = dynamicDocGroupList;
    //     const currentGroupStillValid = currentDocGroupList.find(group => group.documentGroupTypeID === selectedGroupID);
        
    //     // 🔍 SCENARIO 1: Transition from NoCase to WithCase (first case added)
    //     if (resultsCases.length > 0 && originalCaseList.length === 0) {
    //         console.log('🔍 Transitioning from NoCase to WithCase - clearing NoCase document type and narrative');
            
    //         // Clear NoCase selections
    //         setSelectedDocType("");
    //         setNarrativeState("");
    //         if (narrativeForStateRef.current) {
    //             narrativeForStateRef.current.setValue("");
    //         }
            
    //         // Update case data array
    //         const updatedData = caseDataArrayRef.current.map(item => ({
    //             ...item,
    //             documentTypeID: "",
    //             narrative: ""
    //         }));
    //         caseDataArrayRef.current = updatedData;
            
    //         // Switch to WithCase document groups
    //         const withCaseGroupsWithAll = [
    //             { documentGroupTypeID: 0, documentGroupTypeName: "All", documentTypes: [{}] }, 
    //             ...withCaseGroupData
    //         ];
    //         setDocgrouplists(withCaseGroupsWithAll);
    //         setSelectedGroupID(0); // Default to "All"
            
    //         // Update document types for "All" selection
    //         const allWithCaseDocumentTypes = withCaseGroupData.flatMap(group => group.documentTypes || []);
    //         setDocumentTypes(allWithCaseDocumentTypes.sort((a, b) => a.documentTypeName.localeCompare(b.documentTypeName)));
            
    //         console.log('🔍 Switched from NoCase to WithCase document groups');
    //         return; // Exit early
    //     }
        
    //     // 🔍 SCENARIO 2: Transition from WithCase to NoCase (all cases removed)
    //     if (resultsCases.length === 0 && originalCaseList.length > 0) {
    //         console.log('🔍 Transitioning from WithCase to NoCase - preserving original document classification');
            
    //         // Get original document info from the first case
    //         const originalDocumentTypeID = originalCaseList[0]?.originalDocumentTypeID;
    //         const originalDocumentTypeName = originalCaseList[0]?.originalDocumentTypeName;
            
    //         console.log('🔍 Original document info:', {
    //             originalDocumentTypeID,
    //             originalDocumentTypeName
    //         });
            
    //         if (originalDocumentTypeID) {
    //             // 🔍 FIXED: Find the parent group by searching for the document type
    //             let originalGroupID = 0;
    //             let originalGroupData = null;
                
    //             // Search through all document groups to find which one contains this document type
    //             for (const group of allDocumentGroups) {
    //                 if (group.documentTypes && group.documentTypes.some(docType => docType.documentTypeID === originalDocumentTypeID)) {
    //                     originalGroupID = group.documentGroupTypeID;
    //                     originalGroupData = group;
    //                     console.log('🔍 Found document type in group:', {
    //                         groupID: originalGroupID,
    //                         groupName: group.documentGroupTypeName,
    //                         documentTypeID: originalDocumentTypeID
    //                     });
    //                     break;
    //                 }
    //             }
                
    //             if (originalGroupData && originalGroupID > 0) {
    //                 console.log('🔍 Found original group data:', originalGroupData);
                    
    //                 // 🔍 CRITICAL: Update states in the correct order
    //                 // 1. First update the docgrouplists to show NoCase groups
    //                 setDocgrouplists([...withNoCaseGroupData]);
                    
    //                 // 2. Then set the selected values
    //                 setSelectedGroupID(originalGroupID);
    //                 setSelectedDocType(originalDocumentTypeID);
                    
    //                 // 3. Update document types for the selected group
    //                 setDocumentTypes(originalGroupData.documentTypes.sort((a, b) => a.documentTypeName.localeCompare(b.documentTypeName)));
                    
    //                 // 4. Load original narrative
    //                 setTimeout(async () => {
    //                     try {
    //                         const narrativeText = await loadNarrativeText(caseBatchDocumentID, originalGroupID, originalDocumentTypeID, originalDocumentTypeName);
    //                         if (narrativeForStateRef.current) {
    //                             narrativeForStateRef.current.setValue(narrativeText);
    //                         }
    //                         setNarrativeState(narrativeText);
                            
    //                         console.log('🔍 Successfully restored original document classification:', {
    //                             groupID: originalGroupID,
    //                             groupName: originalGroupData.documentGroupTypeName,
    //                             docTypeID: originalDocumentTypeID,
    //                             docTypeName: originalDocumentTypeName
    //                         });
    //                     } catch (error) {
    //                         console.error('🔍 Error loading original narrative:', error);
    //                     }
    //                 }, 100); // Small delay to ensure state updates are processed
    //             } else {
    //                 console.error('🔍 Could not find original group for document type:', originalDocumentTypeID);
    //             }
    //         }
    //         return; // Exit early
    //     }
        
    //     // 🔍 SCENARIO 3: NoCase back to WithCase (case added to document that had original cases but was temporarily empty)
    //     if (resultsCases.length > 0 && originalCaseList.length > 0) {
    //         console.log('🔍 Adding case back to document that originally had cases - restoring to WithCase mode');
            
    //         // Get original document info
    //         const originalDocumentTypeID = originalCaseList[0]?.originalDocumentTypeID;
    //         const originalDocumentTypeName = originalCaseList[0]?.originalDocumentTypeName;
            
    //         console.log('🔍 Restoring WithCase mode with original selections:', {
    //             originalDocumentTypeID,
    //             originalDocumentTypeName
    //         });
            
    //         if (originalDocumentTypeID) {
    //             // Find the parent group for the original document type
    //             let originalGroupID = 0;
    //             let originalGroupData = null;
                
    //             // Search through WithCase groups to find which one contains this document type
    //             for (const group of withCaseGroupData) {
    //                 if (group.documentTypes && group.documentTypes.some(docType => docType.documentTypeID === originalDocumentTypeID)) {
    //                     originalGroupID = group.documentGroupTypeID;
    //                     originalGroupData = group;
    //                     console.log('🔍 Found original document type in WithCase group:', {
    //                         groupID: originalGroupID,
    //                         groupName: group.documentGroupTypeName,
    //                         documentTypeID: originalDocumentTypeID
    //                     });
    //                     break;
    //                 }
    //             }
                
    //             if (originalGroupData && originalGroupID > 0) {
    //                 // Switch to WithCase document groups
    //                 const withCaseGroupsWithAll = [
    //                     { documentGroupTypeID: 0, documentGroupTypeName: "All", documentTypes: [{}] }, 
    //                     ...withCaseGroupData
    //                 ];
    //                 setDocgrouplists(withCaseGroupsWithAll);
                    
    //                 // Set the original group and document type
    //                 setSelectedGroupID(originalGroupID);
    //                 setSelectedDocType(originalDocumentTypeID);
                    
    //                 // Update document types for the selected group
    //                 setDocumentTypes(originalGroupData.documentTypes.sort((a, b) => a.documentTypeName.localeCompare(b.documentTypeName)));
                    
    //                 // Load original narrative
    //                 setTimeout(async () => {
    //                     try {
    //                         const narrativeText = await loadNarrativeText(caseBatchDocumentID, originalGroupID, originalDocumentTypeID, originalDocumentTypeName);
    //                         if (narrativeForStateRef.current) {
    //                             narrativeForStateRef.current.setValue(narrativeText);
    //                         }
    //                         setNarrativeState(narrativeText);
                            
    //                         console.log('🔍 Successfully restored original WithCase classification:', {
    //                             groupID: originalGroupID,
    //                             groupName: originalGroupData.documentGroupTypeName,
    //                             docTypeID: originalDocumentTypeID,
    //                             docTypeName: originalDocumentTypeName
    //                         });
    //                     } catch (error) {
    //                         console.error('🔍 Error loading original narrative:', error);
    //                     }
    //                 }, 100);
                    
    //                 console.log('🔍 Restored original WithCase document classification');
    //                 return; // Exit early
    //             } else {
    //                 // If original group not found in WithCase groups, default to "All"
    //                 console.log('🔍 Original group not found in WithCase groups, defaulting to All');
    //                 const withCaseGroupsWithAll = [
    //                     { documentGroupTypeID: 0, documentGroupTypeName: "All", documentTypes: [{}] }, 
    //                     ...withCaseGroupData
    //                 ];
    //                 setDocgrouplists(withCaseGroupsWithAll);
    //                 setSelectedGroupID(0); // Default to "All"
                    
    //                 // Update document types for "All" selection
    //                 const allWithCaseDocumentTypes = withCaseGroupData.flatMap(group => group.documentTypes || []);
    //                 setDocumentTypes(allWithCaseDocumentTypes.sort((a, b) => a.documentTypeName.localeCompare(b.documentTypeName)));
                    
    //                 // Check if original document type exists in the "All" list
    //                 if (allWithCaseDocumentTypes.some(docType => docType.documentTypeID === originalDocumentTypeID)) {
    //                     setSelectedDocType(originalDocumentTypeID);
                        
    //                     // Load original narrative
    //                     setTimeout(async () => {
    //                         try {
    //                             const narrativeText = await loadNarrativeText(caseBatchDocumentID, 0, originalDocumentTypeID, originalDocumentTypeName);
    //                             if (narrativeForStateRef.current) {
    //                                 narrativeForStateRef.current.setValue(narrativeText);
    //                             }
    //                             setNarrativeState(narrativeText);
    //                         } catch (error) {
    //                             console.error('🔍 Error loading original narrative for All group:', error);
    //                         }
    //                     }, 100);
    //                 } else {
    //                     setSelectedDocType("");
    //                     setNarrativeState("");
    //                     if (narrativeForStateRef.current) {
    //                         narrativeForStateRef.current.setValue("");
    //                     }
    //                 }
                    
    //                 console.log('🔍 Set to All group with original document type check');
    //                 return; // Exit early
    //             }
    //         }
    //     }
        
    //     // 🔍 SCENARIO 4: Handle invalid group selection (when group becomes unavailable)
    //     if (!currentGroupStillValid && selectedGroupID !== 0 && resultsCases.length > 0) {
    //         console.log('🔍 Current group no longer valid, switching to "All"');
            
    //         const hasAllOption = currentDocGroupList.find(group => group.documentGroupTypeID === 0);
    //         const newGroupID = hasAllOption ? 0 : (currentDocGroupList[0]?.documentGroupTypeID || 0);
            
    //         setSelectedGroupID(newGroupID);
    //         setSelectedDocType("");
    //         setNarrativeState("");
    //         if (narrativeForStateRef.current) {
    //             narrativeForStateRef.current.setValue("");
    //         }
            
    //         // Update document types for the new group selection
    //         if (newGroupID === 0) {
    //             const allDocumentTypes = withCaseGroupData.flatMap(group => group.documentTypes || []);
    //             setDocumentTypes(allDocumentTypes.sort((a, b) => a.documentTypeName.localeCompare(b.documentTypeName)));
    //         } else {
    //             const selectedGroup = allDocumentGroups.find(group => group.documentGroupTypeID === newGroupID);
    //             setDocumentTypes(selectedGroup ? selectedGroup.documentTypes.sort((a, b) => a.documentTypeName.localeCompare(b.documentTypeName)) : []);
    //         }
            
    //         console.log(`🔍 Auto-switched document group to: ${newGroupID === 0 ? 'All' : newGroupID}`);
    //     }
        
    // }, [resultsCases.length, originalCaseList.length]); // Minimal dependencies to prevent loops

    const loadInitialDataDbOnly = async (batchDocumentID) => {
        try {
            const loginUserInfo = sessionService.getUser();
            const loginStateID = loginUserInfo ? loginUserInfo.stateID : -123456;
            
            // Load document groups
            const params = {
                clientID: loginStateID
            }
            const docGroupApiData = await ApiManager.GetDocGroupsFromApi(params);
            const allGroups = docGroupApiData[0] || [];
            setAllDocumentGroups(allGroups);

            const withCaseCondition = (item) => item.caseNoCaseFlag === 2 || item.caseNoCaseFlag === 3;
            const withCaseGroupDataFiltered = UtilityLibrary.filterArrayByCondition(allGroups, withCaseCondition);
            setWithCaseGroupData(withCaseGroupDataFiltered);
            const updatedDocGroup = [{ documentGroupTypeID: 0, documentGroupTypeName: "All", documentTypes: [{}] }, ...withCaseGroupDataFiltered]; // 🔍 FIXED: Use withCaseGroupDataFiltered

            const withNoCaseCondition = (item) => item.caseNoCaseFlag === 1 || item.caseNoCaseFlag === 3;
            const withNoCaseGroupDataFiltered = UtilityLibrary.filterArrayByCondition(allGroups, withNoCaseCondition);
            setWithNoCaseGroupData(withNoCaseGroupDataFiltered);
            const updatedDocGroupNoCase = [...withNoCaseGroupDataFiltered]; // 🔍 FIXED: Use withNoCaseGroupDataFiltered

            // Load cases for this batch document
            const paramsCase = {
                batchDocumentID: batchDocumentID
            }
            const caseByBatchDocApiData = await ApiManager.GetCasesFromApiByBatchDocID(paramsCase);
            setCaseByBatchDocData(caseByBatchDocApiData || []);

            // Load batch document details
            const paramsBatchDoc = {
                batchDocumentID: batchDocumentID
            }
            const batchDocApiData = await ApiManager.GetBatchDocumentByIDFromApi(paramsBatchDoc);
            const validatedObject = UtilityLibrary.validateAndStoreObject(batchDocApiData, initialDocumentData);
            setDocumentData(validatedObject);

            // 🔍 FIXED: Properly define all variables including the boolean flags
            let groupID = 0;
            let selectedDocGroupList = updatedDocGroup;
            let selectedGroupData = withCaseGroupDataFiltered;
            let foundInCaseGroups = false; // 🔍 ADDED: Define the missing variable
            let foundInNoCaseGroups = false; // 🔍 ADDED: Define the missing variable

            // Check if the batch document's group type exists in either group list
            if (batchDocApiData.documentGroupTypeID) {
                const groupInCaseData = updatedDocGroup.find(group => group.documentGroupTypeID === batchDocApiData.documentGroupTypeID);
                const groupInNoCaseData = updatedDocGroupNoCase.find(group => group.documentGroupTypeID === batchDocApiData.documentGroupTypeID);
                
                if (groupInCaseData) {
                    groupID = batchDocApiData.documentGroupTypeID;
                    selectedDocGroupList = updatedDocGroup;
                    selectedGroupData = withCaseGroupDataFiltered;
                    foundInCaseGroups = true; // 🔍 ADDED: Set the flag
                } else if (groupInNoCaseData) {
                    groupID = batchDocApiData.documentGroupTypeID;
                    selectedDocGroupList = updatedDocGroupNoCase;
                    selectedGroupData = withNoCaseGroupDataFiltered;
                    foundInNoCaseGroups = true; // 🔍 ADDED: Set the flag
                } else {
                    // If documentGroupTypeID doesn't exist in either list, fall back to finding by document type
                    const parentGroup = updatedDocGroup.find((group) =>
                        Array.isArray(group["documentTypes"]) &&
                        group["documentTypes"].some(
                            (child) => child["documentTypeID"] === batchDocApiData.documentTypeID
                        )
                    );
                    
                    const parentNoCaseGroup = updatedDocGroupNoCase.find((group) =>
                        Array.isArray(group["documentTypes"]) &&
                        group["documentTypes"].some(
                            (child) => child["documentTypeID"] === batchDocApiData.documentTypeID
                        )
                    );

                    if (parentGroup) {
                        groupID = parentGroup.documentGroupTypeID;
                        selectedDocGroupList = updatedDocGroup;
                        selectedGroupData = withCaseGroupDataFiltered;
                        foundInCaseGroups = true; // 🔍 ADDED: Set the flag
                    } else if (parentNoCaseGroup) {
                        groupID = parentNoCaseGroup.documentGroupTypeID;
                        selectedDocGroupList = updatedDocGroupNoCase;
                        selectedGroupData = withNoCaseGroupDataFiltered;
                        foundInNoCaseGroups = true; // 🔍 ADDED: Set the flag
                    }
                }
            } else {
                // If no documentGroupTypeID, fall back to finding by document type
                const parentGroup = updatedDocGroup.find((group) =>
                    Array.isArray(group["documentTypes"]) &&
                    group["documentTypes"].some(
                        (child) => child["documentTypeID"] === batchDocApiData.documentTypeID
                    )
                );
                
                const parentNoCaseGroup = updatedDocGroupNoCase.find((group) =>
                    Array.isArray(group["documentTypes"]) &&
                    group["documentTypes"].some(
                        (child) => child["documentTypeID"] === batchDocApiData.documentTypeID
                    )
                );

                if (parentGroup) {
                    groupID = parentGroup.documentGroupTypeID;
                    selectedDocGroupList = updatedDocGroup;
                    selectedGroupData = withCaseGroupDataFiltered;
                    foundInCaseGroups = true; // 🔍 ADDED: Set the flag
                } else if (parentNoCaseGroup) {
                    groupID = parentNoCaseGroup.documentGroupTypeID;
                    selectedDocGroupList = updatedDocGroupNoCase;
                    selectedGroupData = withNoCaseGroupDataFiltered;
                    foundInNoCaseGroups = true; // 🔍 ADDED: Set the flag
                }
            }

            // 🔍 FIXED: Lines 772-774 - Use the properly defined variables
            if (docGroupApiData.length > 0) {
                if (foundInCaseGroups) { // 🔍 FIXED: Now properly defined
                    setDocgrouplists(updatedDocGroup); // Use version with "All" option
                } else if (foundInNoCaseGroups) { // 🔍 FIXED: Now properly defined
                    setDocgrouplists(updatedDocGroupNoCase); // Use version without "All" option
                } else {
                    // If no match found, default to no-case groups since this is "Unidentified"
                    setDocgrouplists(updatedDocGroupNoCase);
                    selectedGroupData = withNoCaseGroupDataFiltered;
                }
            } else {
                setDocgrouplists([]);
            }

            const docTypeID = batchDocApiData.documentTypeID;

            // Get the original document type name from the correct group data
            const selectedGroup = selectedGroupData.find(group => group.documentGroupTypeID === groupID);
            const originalDocTypeName = selectedGroup ? 
                (selectedGroup.documentTypes.find((docType) => docType.documentTypeID === docTypeID)?.documentTypeName || "") : "";

            // Create case list with original document type ID
            const newCaseList = (caseByBatchDocApiData || []).map((item) => {
                const newItem = {};
                fieldsForCaseList.forEach((field) => {
                    if (field in item) {
                        newItem[field] = item[field];
                    }
                });
                newItem['isNewlyAdded'] = false;
                newItem['originalDocumentTypeID'] = docTypeID;
                newItem['originalDocumentTypeName'] = originalDocTypeName;
                newItem['originalDocumentGroupTypeID'] = groupID; 
                newItem['originalDocumentGroupTypeName'] = selectedGroup?.documentGroupTypeName || ""; 
                newItem['originalLastIndexTS'] = batchDocApiData.lastIndexTS;
                return newItem;
            });

            setCaseResults(newCaseList);
            setOriginalCaseList(newCaseList);

            // Set document group and type dropdowns
            setSelectedGroupID(groupID);
            let narrativeText = "";

            // Configure document types based on selected group
            if (groupID === 0) {
                const allDocumentTypes = selectedGroupData.flatMap(group => group.documentTypes || []);
                setDocumentTypes(allDocumentTypes.sort((a, b) => a.documentTypeName.localeCompare(b.documentTypeName)));
                
                if (docTypeID > 0 && allDocumentTypes.some(docType => docType.documentTypeID === docTypeID)) {
                    setSelectedDocType(docTypeID);
                    
                    const selectedDocTypeText = allDocumentTypes.find(docType => docType.documentTypeID === docTypeID)?.documentTypeName || "";
                    narrativeText = await loadNarrativeText(batchDocumentID, groupID, docTypeID, selectedDocTypeText);
                } else {
                    setSelectedDocType("");
                }
            } else {
                const selectedGroup = selectedGroupData.find(group => group.documentGroupTypeID === groupID);
                setDocumentTypes(selectedGroup ? selectedGroup.documentTypes.sort((a, b) => a.displayOrder - b.displayOrder) : []);
                setSelectedDocType(docTypeID);

                const selectedDocTypeText = selectedGroup ? 
                    (selectedGroup.documentTypes.find((option) => option.documentTypeID === docTypeID)?.documentTypeName || "") : "";

                narrativeText = await loadNarrativeText(batchDocumentID, groupID, docTypeID, selectedDocTypeText);
            }

            // Set narrative text
            if (narrativeForStateRef.current) {
                narrativeForStateRef.current.setValue(narrativeText);
            }
            setNarrativeState(narrativeText);
            setOriginalNarrative(narrativeText);
            setOriginalActionNotes("");
            setFormValues(prev => ({
                ...prev,
                docGroupSelect: groupID,
                docTypeSelect: docTypeID,
                narrativeForStateTxt: narrativeText
            }));

            originalDocType = docTypeID;

            // Prepare final case data with all required fields
            const combinedArray = UtilityLibrary.combineObjectWithArray(validatedObject, (caseByBatchDocApiData || []));
            const finalCaseData = combinedArray.map(item => ({
                ...item,
                isNewlyAdded: false,
                crudTypeID: 100,
                documentGroupTypeID: groupID,
                documentTypeID: batchDocApiData.documentTypeID,
                originalDocumentTypeID: docTypeID, 
                originalDocumentTypeName: originalDocTypeName, 
                originalDocumentGroupTypeID: groupID, 
                originalDocumentGroupTypeName: selectedGroup?.documentGroupTypeName || "", 
                originalLastIndexTS: batchDocApiData.lastIndexTS,
                narrative: narrativeText 
            }));
            
            caseDataArrayRef.current = finalCaseData;

            console.log('🔍 Selected group ID:', groupID);
            console.log('🔍 Found in case groups:', foundInCaseGroups);
            console.log('🔍 Found in no-case groups:', foundInNoCaseGroups);
            console.log('🔍 Using document group list:', foundInCaseGroups ? 'Case groups' : 'No-case groups');
            
            setInitialFormValues({ ...formValues, docGroupSelect: groupID, docTypeSelect: docTypeID });

        } catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "loadInitialDataDbOnly", {});
        }
    };




    
    const copyToClipboardFallback = (text) => {
        // Create a temporary textarea element
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed'; // Avoid scrolling to the bottom
        textarea.style.top = '0';
        textarea.style.left = '0';
        textarea.style.opacity = '0'; // Make it invisible
        document.body.appendChild(textarea);

        // Select and copy the text
        textarea.select();
        textarea.setSelectionRange(0, text.length); // Ensure all text is selected
        try {
            const successful = document.execCommand('copy');
            if (successful) {
                console.log('Text successfully copied to clipboard');
                //alert('Text copied to clipboard!'); // Notify the user
            } else {
                throw new Error('Failed to copy text using execCommand');
            }
        } catch (err) {
            console.error('Failed to copy text: ', err);
            //alert('Failed to copy text. Please try again.'); // Notify the user
        }

        // Clean up
        document.body.removeChild(textarea);
    };

    const copyToClipboard = async (textToCopy) => {
        //const textToCopy = textareaRef.current.value;

        if (navigator.clipboard) {
            await navigator.clipboard.writeText(textToCopy)
                .then(() => {
                    console.log('Text successfully copied to clipboard');
                    //alert('Text copied to clipboard!'); // Optional: Notify the user
                })
                .catch((err) => {
                    console.error('Failed to copy text: ', err);
                    // Fallback to execCommand if Clipboard API fails
                    copyToClipboardFallback(textToCopy);
                });
        } else {
            // Use fallback if Clipboard API is not supported
            copyToClipboardFallback(textToCopy);
        }
    };

    const copyNarrativeToClipboard = async () => {
        try {
            if (siteSettingDetail.NarrativeCopyToClipboardManageKeywords === EnumUtility.NarrativeCopyToClipboard.Allow) {
                const narrativeText = narrativeForStateRef.current ? narrativeForStateRef.current?.getValue() : "";
                await copyToClipboard(narrativeText);
            }
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "copyNarrativeToClipboard", {});
        }
    };

    const handleSaveClick = async () => {
        setIsPageLoading(true);
        let isSavedSuccess = false;
        try {

            const isMainframeIntegration = UtilityLibrary.DetermineIfMainframeHost();
  

            if (isMainframeIntegration) {
                if (UtilityLibrary.isUsingMockData() == false) {
                    const canContinue = await UtilityLibrary.checkMainframeConnectivityWithErrorHandling(
                        configLibrary.apiMainframeUrl, 
                        handleNotificationDialog
                    );

                    if (!canContinue) {
                        setIsPageLoading(false);
                        return; // Exit early if mainframe is not reachable
                    }
                }
            }

            await copyNarrativeToClipboard();
            await simulateAsyncDelay();

            const loginUserInfo = sessionService.getUser();
            const loginLegacyUserID = loginUserInfo ? loginUserInfo.legacyUserID : -123456;
            const loginStateID = loginUserInfo ? loginUserInfo.stateID : -123456;
            const loginSiteID = loginUserInfo ? loginUserInfo.siteID : -123456;
            const currentDateTime = new Date().toISOString();

            const enableTransmit = documentTypes.find((option) => option.documentTypeID === selectedDocType)?.enableTransmit ||false
            const documentStatusID = (loginStateID === EnumUtility.StateIDKey.Mississippi.ID && enableTransmit) ? 41 : 5;

            const finalCaseData = caseDataArrayRef.current.map(item => {
            // 🔍 SIMPLE FIX: Use documentData.documentTypeID for missing originalDocumentTypeID
                const originalDocumentTypeID = item.originalDocumentTypeID || documentData.documentTypeID || "";
                let originalDocumentTypeName = item.originalDocumentTypeName || "";
                
                // If originalDocumentTypeName is missing, find it from documentTypes array using documentData.documentTypeID
                if (!originalDocumentTypeName && documentData.documentTypeID) {
                    const foundDocType = allDocumentGroups
                        .flatMap(group => group.documentTypes || [])
                        .find(docType => docType.documentTypeID === documentData.documentTypeID);
                    
                    originalDocumentTypeName = foundDocType ? foundDocType.documentTypeName : "";
                }

                return {
                    ...item, // Preserve existing properties of the object
                    notes: notesForAction, // Update field1 with the new value
                    narrative: narrativeState,  // Update field2 with the new value
                    documentGroupTypeID: selectedGroupID,
                    documentTypeID: selectedDocType,
                    userLoginID: loginLegacyUserID,
                    yTracSiteID: loginSiteID,
                    masterServerDBSchema,
                    activityStartTS: timeActionStart,
                    timeZoneName: timeZone,
                    documentLocation: documentData.documentLocation,
                    originalDocumentTypeID: originalDocumentTypeID, // 🔍 ADDED: Ensure this is always set
                    originalDocumentTypeName: originalDocumentTypeName, // 🔍 ADDED: Ensure this is always set
                    documentStatusID: documentStatusID
                };
            });
            // setCaseDataArray(finalCaseData);
            caseDataArrayRef.current = finalCaseData

            const postResponseData = await ApiManager.PostManageKeywordToApi(finalCaseData);
            if (postResponseData) {
                isSavedSuccess = true;
                
                // iConnect Implementation: Newly added cases have already been processed individually
                // during addNewCaseInfo, following the NextWorkItem pattern
                if (loginStateID === EnumUtility.StateIDKey.Mississippi.ID && newlyAddedCases.length > 0) {
                    console.log(`ManageKeywords: iConnect processing completed for ${newlyAddedCases.length} newly added case(s) during save operation`);
                    console.log('ManageKeywords: Cases processed for iConnect:', newlyAddedCases.map(c => c.caseNumber));
                    
                    // Clear the newly added cases list after save
                    setNewlyAddedCases([]);
                }

                //await navigator.clipboard.writeText(narrativeState);
                //handleNotificationDialog(EnumUtility.NotificationAlerts.ManageKeywordsSuccess);
            }
            else {
                handleNotificationDialog(EnumUtility.NotificationAlerts.ManageKeywordsFailure);
            }

            const sessionLoginUser = sessionService.getUser();
            //const isMainframeIntegration = UtilityLibrary.DetermineIfMainframeHost();
            const isAPIIntegration = UtilityLibrary.DetermineIfAPIHost();
            
            if (isMainframeIntegration || (isAPIIntegration && siteSettingDetail.ApiWriteNarrative === "1")) {

                const addedCases = resultsCases.filter(displayedCase => 
                    !originalCaseList.some(originalCase => originalCase.caseNumber === displayedCase.caseNumber)
                );
                
                const removedCases = originalCaseList.filter(originalCase => 
                    !resultsCases.some(displayedCase => displayedCase.caseNumber === originalCase.caseNumber)
                );

                const casesWithChangedDocType = resultsCases.filter(currentCase => {

                    const originalCase = originalCaseList.find(origCase => origCase.caseNumber === currentCase.caseNumber);
                    
                    if (originalCase) {
                        return originalCase.originalDocumentTypeID !== selectedDocType;
                    }
                    
                    return false;
                });

                const currentNarrativeText = narrativeForStateRef.current ? narrativeForStateRef.current.getValue() : narrativeState;

                if (addedCases.length > 0) {  
                            
                    for (const addedCase of addedCases) {
                        try {
                            // const paramsForPush = {
                            // stateID: loginStateID,
                            // apiMainframeUrl: configLibrary.apiMainframeUrl,
                            // caseNumber: addedCase.caseNumber,
                            // narrativeText: currentNarrativeText,
                            // };
                            await UtilityLibrary.writeExternalNarrative(loginStateID, false, currentNarrativeText, {
                                caseNumber: addedCase.caseNumber,
                                onFailure: showNarrativeUpdateFailure,
                                formName: null
                            });

                        } catch (error) {
                            showNarrativeUpdateFailure();
                        }
                    }
                }

                if (removedCases.length > 0) {

                    for (const removedCase of removedCases) {
                        try {
    
                            const context = {
                                originalDocumentTypeName: removedCase.originalDocumentTypeName,
                                originalLastIndexTS: removedCase.originalLastIndexTS
                            };

                            const paramsForG4Narr = {
                                YTracSiteID: loginSiteID,
                                NoteTemplateID: MasterEnum.NoteTemplate.Document_Terminate_Workflow
                            };

                            const removedCaseNarrative = await UtilityLibrary.NarrativeBuilder(paramsForG4Narr, context);

                            // const paramsForPush = {
                            // stateID: loginStateID,
                            // apiMainframeUrl: configLibrary.apiMainframeUrl,
                            // caseNumber: removedCase.caseNumber,
                            // narrativeText: removedCaseNarrative,
                            // };
                            await UtilityLibrary.writeExternalNarrative(loginStateID, false, removedCaseNarrative, {
                                caseNumber: removedCase.caseNumber,
                                onFailure: showNarrativeUpdateFailure,
                                formName: null
                            });

                        } catch (error) {
                            showNarrativeUpdateFailure();
                        }
                    }
                    
                    console.log('🔍 Finished processing all removed cases');
                }

                if (casesWithChangedDocType.length > 0) {

                    for (const changedCase of casesWithChangedDocType) {
                        try {
                            console.log(`🔍 Pushing updated narrative for case with changed doc type: ${changedCase.caseNumber}`);

                            // const paramsForPush = {
                            //     stateID: loginStateID,
                            //     apiMainframeUrl: configLibrary.apiMainframeUrl,
                            //     caseNumber: changedCase.caseNumber,
                            //     narrativeText: currentNarrativeText, // Use current narrative (with new doc type)
                            // };
                            await UtilityLibrary.writeExternalNarrative(loginStateID, false, currentNarrativeText, {
                                caseNumber: changedCase.caseNumber,
                                onFailure: showNarrativeUpdateFailure,
                                formName: null
                            });

                            const context = {
                                originalDocumentTypeName: changedCase.originalDocumentTypeName,
                                originalLastIndexTS: changedCase.originalLastIndexTS
                            };

                            const paramsForG4Narr = {
                                YTracSiteID: loginSiteID,
                                NoteTemplateID: MasterEnum.NoteTemplate.Document_Terminate_Workflow
                            };

                            const removedCaseNarrative = await UtilityLibrary.NarrativeBuilder(paramsForG4Narr, context);

                            // const paramsForRevertPush = {
                            //     stateID: loginStateID,
                            //     apiMainframeUrl: configLibrary.apiMainframeUrl,
                            //     caseNumber: changedCase.caseNumber,
                            //     narrativeText: removedCaseNarrative, // Use the built narrative for the original doc type
                            // };
                            
                            await UtilityLibrary.writeExternalNarrative(loginStateID, false, removedCaseNarrative, {
                                caseNumber: changedCase.caseNumber,
                                onFailure: showNarrativeUpdateFailure,
                                formName: null
                            });


                        } catch (error) {
                            showNarrativeUpdateFailure();
                        }
                    }
                    
                }

                if (addedCases.length > 0 && stateID === EnumUtility.StateIDKey.Mississippi.ID) {
               
                    const selectedDocTypeText = documentTypes.find((option) => option.documentTypeID === selectedDocType)?.documentTypeName || "";

                    // 🔍 ADDED: Get case data from caseDataArrayRef that contains case numbers present in addedCases
                    const addedCaseData = caseDataArrayRef.current.filter(caseItem => 
                        addedCases.some(addedCase => addedCase.caseNumber === caseItem.caseNumber)
                    );
                    if (enableTransmit) {// process iconnect if enabletrasmit for the doctype is true
                         // Process each added case for iConnect
                        for (const caseData of addedCaseData) {
                            try {
                                // Create enhanced case data with SSN information
                                const enhancedCaseData = {
                                    ...caseData,
                                    batchID: documentData?.batchID || 0,
                                    batchDocumentID: caseBatchDocumentID || documentData?.batchDocumentID || 0,
                                    stateDocTypeName: selectedDocTypeText,
                                    addedTimestamp: Date.now()
                                };

                                console.log(`🔍 Processing iConnect for added case: ${caseData.caseNumber}`);
                                await processIConnectForNewCase(enhancedCaseData, selectedDocTypeText);

                            } catch (error) {
                                console.error(`🔍 Error processing iConnect for case ${caseData.caseNumber}:`, error);
                            }
                        }
                    }
                }

            }

        }
        catch (error) { //TODO call to mainframe has not been implemented yet
           const errorType = error?.Details?.type;
            if ([ EnumUtility.CaseSearchResultCode.MFSessionDisconnectError.Name,
                EnumUtility.CaseSearchResultCode.ScraperDisconnectedError.Name].includes(errorType)) {
                let messageContentID = null;
                if (errorType === EnumUtility.CaseSearchResultCode.MFSessionDisconnectError.ID){
                    messageContentID = ClientType.MessageContent.E_MF7049_Lost_Connection_To_State_System
                }
                else{
                    messageContentID = ClientType.MessageContent.E_UI7056_Scrapper_Connection_Status;
                }
                const messageContent = await SharedService.getMessageContent(messageContentID)
                handleNotificationDialog({ NotificationType: 'error', Message: messageContent?.messageText });
            }
            await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleSaveClick");
        }
        finally {
            setIsPageLoading(false);
            if (isSavedSuccess) {
                setLoadingComplete(true); // Trigger Keyword disabling
            }
        }
    };


    const handleGroupChange = async (event, newValue, reason) => {
        // 🔍 SIMPLIFIED LOGIC
        try {
            const newGroupID = newValue ? newValue.documentGroupTypeID : selectedGroupID;
            
            // If the group isn't actually changing, do nothing.
            if (newGroupID === selectedGroupID && reason !== 'clear') return;

            const groupIDToSet = (reason === 'clear' || !newValue) ? '' : newGroupID;

            console.log('🔍 Group change:', { from: selectedGroupID, to: groupIDToSet });

            // Set the new group ID
            setSelectedGroupID(groupIDToSet);

            // Always clear the doc type and narrative when the group changes
            setSelectedDocType("");
            setNarrativeState("");
            if (narrativeForStateRef.current) {
                narrativeForStateRef.current.setValue("");
            }

            // Determine the new list of document types
            let newDocumentTypes = [];
            if (groupIDToSet === 0) {
                // "All" is selected, so flatten all with-case groups
                newDocumentTypes = withCaseGroupData.flatMap(group => group.documentTypes || []);
            } else if (groupIDToSet) {
                // A specific group is selected
                const selectedGroup = allDocumentGroups.find(group => group.documentGroupTypeID === groupIDToSet);
                newDocumentTypes = selectedGroup ? (selectedGroup.documentTypes || []) : [];
            }
            
            setDocumentTypes(newDocumentTypes.sort((a, b) => a.documentTypeName.localeCompare(b.documentTypeName)));

            // Update form values
            setFormValues(formValues)
            // Trigger validation with the cleared doc type
            debouncedValidation("", resultsCases, formValues, isConfidential);

        } catch (error) {
            await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleGroupChange");
        }
    };

    const loadNarrativeText = async (batchDocID, docGroupID, docTypeID, docTypeName) => {

        try {
            // Load Narrative content based on the document type selected
            const loginUserInfo = sessionService.getUser();
            const loginStateID = loginUserInfo ? loginUserInfo.stateID : -123456;
            const userLogin = JSON.parse(SessionManager.GetValueFromSession(SessionManager.UserLoginDetailsKey));
            const loginUsername = userLogin ? userLogin.loginName : "";
            
            const narrativeObj = new NarrativeModel();
            narrativeObj.ytracSiteID = loginStateID;
            narrativeObj.batchDocumentID = batchDocID;
            narrativeObj.docGroupID = docGroupID;
            narrativeObj.documentTypeID = docTypeID;
            narrativeObj.documentTypeName = docTypeName;
            narrativeObj.documentSourceTypeName = "";
            narrativeObj.noCase = false;
            narrativeObj.caseNumber = "";
            narrativeObj.loginName = loginUsername?.toLowerCase();
            narrativeObj.masterServerDBSchema = masterServerDBSchema;
            narrativeObj.SystemTimeZoneName = loginUserInfo?.timeZone;

            const narrativeApiData = await ApiManager.GetNarrativeForKeywordsFromApi(narrativeObj.toObject());
            let narrativeText = "";
            if (narrativeApiData.length > 0) {
                narrativeText = narrativeApiData[0];
            }

            return narrativeText;
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "loadNarrativeText", {});
        }
    };

    const callBuildNarrative = async (noteTemplateID, documentTypeName, lastIndexTS) => {

        try {
            // Load Narrative content based on the document type selected
            const loginUserInfo = sessionService.getUser();
            const loginStateID = loginUserInfo ? loginUserInfo.stateID : -123456;
            const loginSiteID = loginUserInfo ? loginUserInfo.siteID : -123456;
            const userLogin = JSON.parse(SessionManager.GetValueFromSession(SessionManager.UserLoginDetailsKey));
            const loginUsername = userLogin ? userLogin.loginName : "";
            
            const narrativeObj = new NarrativeBuilderModel();
            narrativeObj.ytracSiteID = loginSiteID;
            narrativeObj.noteTemplateID = noteTemplateID;
            narrativeObj.documentTypeName = documentTypeName;
            narrativeObj.lastIndexTS = lastIndexTS;
            narrativeObj.loginName = loginUsername?.toLowerCase();
            narrativeObj.masterServerDBSchema = masterServerDBSchema;
            narrativeObj.yTracSite_StateID = loginStateID;
            narrativeObj.timeZoneName = loginUserInfo?.timeZone;

            const narrativeApiData = await ApiManager.BuildNarrativeFromApi(narrativeObj.toObject());
            let narrativeText = "";
            if (narrativeApiData.length > 0) {
                narrativeText = narrativeApiData[0];
            }

            return narrativeText;
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "loadNarrativeText", {});
        }
    };

    const handleDocType = async (event, newValue, reason) => {
        setIsPageLoading(true);
        try {
            await simulateAsyncDelay();

            let value = "";
            let narrativeText = ""; 

            if (reason !== "clear") {
                value = newValue.documentTypeID;
            } 

            let groupID = selectedGroupID;

            // 🔍 FIXED: If "All" is selected, find the parent group and update selectedGroupID
            if (groupID === 0 && value) {
                groupID = getParentIdByChildId(docgrouplists, 'documentGroupTypeID', 'documentTypes', 'documentTypeID', value);
                setSelectedGroupID(groupID); // 🔍 ADDED: Update the selected group ID state
            }

            if (value && value !== "") {
                const selectedDocTypeText = documentTypes.find((option) => option.documentTypeID === value)?.documentTypeName || "";
                narrativeText = await loadNarrativeText(caseBatchDocumentID, groupID, value, selectedDocTypeText);
            }

            if (narrativeForStateRef.current) {
                narrativeForStateRef.current.setValue(narrativeText);
            }
            setNarrativeState(narrativeText);

            const updatedData = caseDataArrayRef.current.map(item => ({
                ...item,
                documentGroupTypeID: groupID,
                documentTypeID: value,  // Set the new value for the 'document type' field
                narrative: narrativeText,
            }));
            const updatedFormValues = { ...formValues, [event.target.name]: value, narrativeForStateTxt: narrativeText };

            // setCaseDataArray(updatedData);
            caseDataArrayRef.current = updatedData
            setSelectedDocType(value);
            //Update form values
            setFormValues(updatedFormValues);

            debouncedValidation(value, updatedData, updatedFormValues, isConfidential);
        }
        catch (error) {
            await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleDocType");
        }
        finally { setIsPageLoading(false); }
    };

    /**************************************************************************** */

    // Helper function to convert string to Pascal Case
    const toPascalCase = (str) => {
        return str
            .toLowerCase()
            .replace(/\s+/g, ' ') // Normalize multiple spaces
            .trim() // Remove leading and trailing spaces
            .replace(/\b\w/g, (char) => char.toUpperCase()); // Capitalize each word
    };

    const simulateAsyncDelay = async () => {
        await new Promise((resolve) => setTimeout(resolve, 0));
    };

    const AddDocToNewCase = async (newCaseData, confidential) => {
        try {
            // Check if this case was originally on the document
            const wasOriginallyOnDocument = originalCaseList.some(originalCase => 
                originalCase.caseNumber === newCaseData.caseNumber
            );

            let originalDocumentTypeName = originalCaseList?.[0]?.originalDocumentTypeName 
            let originalDocumentTypeID = originalCaseList?.[0]?.originalDocumentTypeID 
            let originalDocumentGroupTypeID = originalCaseList?.[0]?.originalDocumentGroupTypeID 
            let originalDocumentGroupTypeName = originalCaseList?.[0]?.originalDocumentGroupTypeName 
            let resetDocGrpNdType = false;

            // 🔍 FIXED: Create the missing newArray object
            const isTransitioningFromNoCase = originalCaseList.length === 0 && resultsCases.length === 0;
            if (isTransitioningFromNoCase){
                originalDocumentGroupTypeID = "";
                originalDocumentGroupTypeName = "";
                originalDocumentTypeName = "";
                originalDocumentTypeID = "";
                resetDocGrpNdType = true;
                const group = withCaseGroupData.find(group => selectedGroupID === group.documentGroupTypeID)
                if (group){
                    originalDocumentGroupTypeID = group.documentGroupTypeID;
                    originalDocumentGroupTypeName = group.documentGroupTypeName;
                    resetDocGrpNdType = false;
                    const docType = group.documentTypes.find(docType => selectedDocType === docType.documentTypeID)
                    if (docType){
                        originalDocumentTypeName = docType.documentTypeName;
                        originalDocumentTypeID = docType.documentTypeID;
                    }
                }
            }

            const originalLastIndexTS = originalCaseList.length > 0 ? 
                originalCaseList[0].originalLastIndexTS : 
                documentData?.lastIndexTS;
            
            // 🔍 ADDED: Create the newArray that was missing
            const newArray = {
                "batchDocumentID": caseBatchDocumentID,
                "caseNumber": newCaseData.caseNumber,
                "cpName": newCaseData.cpFullName,
                "ncpName": newCaseData.ncpFullName,
                "isNewlyAdded": !wasOriginallyOnDocument, // False if it was originally on the document
                "originalDocumentTypeID": originalDocumentTypeID,
                "originalDocumentTypeName": originalDocumentTypeName,
                "originalDocumentGroupTypeID": originalDocumentGroupTypeID,
                "originalDocumentGroupTypeName": originalDocumentGroupTypeName,
                "originalLastIndexTS": originalLastIndexTS
            };

            const newCase = UtilityLibrary.getIdByValueFromArray(resultsCases, "caseNumber", newCaseData.caseNumber);

            if (newCase === 0) {
                const updatedCaseData = UtilityLibrary.AddRecordToArray(newArray, resultsCases, "caseNumber");
                setCaseResults(updatedCaseData);
                
                // 🔍 FIXED: Determine if this is truly the first case or just adding to a document that had cases removed
                const isFirstCaseOnDocument = resultsCases.length === 0 && originalCaseList.length === 0; // True NoCase to WithCase
                const isAddingToDocumentThatHadCases = resultsCases.length === 0 && originalCaseList.length > 0; // Adding to document that had cases removed
                
                if (isFirstCaseOnDocument) {
                    console.log('🔍 First case added to NoCase document - transitioning from NoCase to WithCase');
                    
                    // RC commented this part narrative should load on nocase transition
                    // Clear document type and narrative for NoCase transition
                    // setSelectedDocType("");
                    // setNarrativeState("");
                    // if (narrativeForStateRef.current) {
                    //     narrativeForStateRef.current.setValue("");
                    // }
                    
                    // Update case data array to clear narrative
                    // const clearedData = caseDataArrayRef.current.map(item => ({
                    //     ...item,
                    //     documentTypeID: originalDocumentTypeID, // Clear document type
                    //     narrative: "" // Clear narrative
                    // }));
                    // caseDataArrayRef.current = clearedData;
                    
                    if (resetDocGrpNdType) {
                         setTimeout(() => {
                        setSelectedGroupID(0); // Set to "All"
                        // Update document types for "All" selection using WithCase groups
                        const allDocumentTypes = withCaseGroupData.flatMap(group => group.documentTypes || []);
                        setDocumentTypes(allDocumentTypes.sort((a, b) => a.documentTypeName.localeCompare(b.documentTypeName)));
                        
                        // 🔍 FIXED: Call validation after state updates complete
                        setTimeout(() => {
                            const docTypeForValidation = "";
                            debouncedValidation(docTypeForValidation, updatedCaseData, formValues, confidential);
                        }, 100);
                    }, 100);
                    }
                    else {
                     // RC Added this to enable Save if grouptype or doc type is not unidentified and no case transition is happening
                        debouncedValidation(originalDocumentTypeName, updatedCaseData, formValues, confidential);
                    }
                   
                } else if (isAddingToDocumentThatHadCases) {
                    // 🔍 FIXED: Adding case to document that had cases removed - document type should already be restored
                    console.log('🔍 Adding case to document that had cases removed - using restored selectedDocType:', selectedDocType);
                    
                    // Use the current selectedDocType for validation (should be the restored original doc type)
                    const currentDocType = selectedDocType || "";
                    const updatedFormValues = {
                        ...formValues,
                        caseNumberTxt: newCaseData.caseNumber,
                        caseNumberSearched: newCaseData.caseNumber,
                    };
                    
                    // 🔍 CRITICAL: Call validation immediately 
                    debouncedValidation(currentDocType, updatedCaseData, updatedFormValues, confidential);
                } else {
                    // 🔍 FIXED: Adding case to document that already has cases
                    console.log('🔍 Adding additional case to document - using current selectedDocType:', selectedDocType);
                    
                    // Use the current selectedDocType for validation
                    const currentDocType = selectedDocType || "";
                    const updatedFormValues = {
                        ...formValues,
                        caseNumberTxt: newCaseData.caseNumber,
                        caseNumberSearched: newCaseData.caseNumber,
                    };
                    
                    // 🔍 CRITICAL: Call validation immediately for existing document case addition
                    debouncedValidation(currentDocType, updatedCaseData, updatedFormValues, confidential);
                }
                
                console.log('🔍 Added case:', {
                    caseNumber: newCaseData.caseNumber,
                    wasOriginallyOnDocument,
                    isNewlyAdded: !wasOriginallyOnDocument,
                    isFirstCaseOnDocument,
                    isAddingToDocumentThatHadCases,
                    currentSelectedDocType: selectedDocType,
                    originalCaseListLength: originalCaseList.length,
                    resultsCasesLength: resultsCases.length
                });
                
                setNewCasesAdded(true)
                return true;
            }
            else {
                setNewCasesAdded(false)
                return false;
            }

        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "AddDocToNewCase", {});
        }
    };

    const MergeDocToNewCase = async (newCaseData) => {
        try {
            // 🔍 FIXED: Check if this case was originally on the document
            const wasOriginallyOnDocument = originalCaseList.some(originalCase => 
                originalCase.caseNumber === newCaseData.caseNumber
            );

            // 🔍 FIXED: Check if this case was previously marked for deletion (crudTypeID: 200)
            const existingCaseInArray = caseDataArrayRef.current.find(item => 
                item.caseNumber === newCaseData.caseNumber
            );

            let crudTypeID, isNewlyAdded;

            if (existingCaseInArray && existingCaseInArray.crudTypeID === 200) {
                // 🔍 ADDED: Case was marked for deletion, now being restored
                console.log('🔍 Restoring case that was marked for deletion:', newCaseData.caseNumber);
                
                if (wasOriginallyOnDocument) {
                    // Restore to original state
                    crudTypeID = 100; // Update
                    isNewlyAdded = false;
                    
                    // Update the existing entry instead of adding a new one
                    modifyCaseArray(newCaseData.caseNumber, true); // true = isRestoring
                    return; // Exit early since we updated the existing entry
                } else {
                    // This was a new case that was added then removed, now being re-added
                    crudTypeID = 300; // Insert
                    isNewlyAdded = true;
                }
            } else {
                // 🔍 FIXED: Normal case addition logic
                crudTypeID = wasOriginallyOnDocument ? 100 : 300; // 100 = Update, 300 = Insert
                isNewlyAdded = !wasOriginallyOnDocument;
            }

            const updatedCaseData = { 
                ...newCaseData, 
                isNewlyAdded: isNewlyAdded,
                crudTypeID: crudTypeID 
            };
            
            const mergedArray = mergeArrays([updatedCaseData], caseByBatchDocData, casefieldMapping1, casefieldMapping2);
            const combinedArray = UtilityLibrary.combineObjectWithArray(documentData, mergedArray);
            
            // Check for duplicates
            const isDuplicate = caseDataArrayRef.current.some(
                (record) => record["caseNumber"] === combinedArray[0].caseNumber);
            
            if (!isDuplicate) {
                caseDataArrayRef.current.push(combinedArray[0]);
            }

            console.log('🔍 Merged case data:', {
                caseNumber: newCaseData.caseNumber,
                wasOriginallyOnDocument,
                crudTypeID,
                isNewlyAdded,
                wasRestoredFromDeletion: existingCaseInArray && existingCaseInArray.crudTypeID === 200
            });
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "MergeDocToNewCase", {});
        }
    };

    // Function to remove or update an item based on its condition
    const modifyCaseArray = (caseNumber, isRestoring = false) => {
        try {
            const updatedData = caseDataArrayRef.current.map(item => {
                if (item.caseNumber === caseNumber) {
                    if (isRestoring) {
                        // 🔍 ADDED: Restore the case to its original state
                        const wasOriginallyOnDocument = originalCaseList.some(originalCase => 
                            originalCase.caseNumber === caseNumber
                        );
                        
                        if (wasOriginallyOnDocument) {
                            // Restore to original state (Update)
                            return { ...item, crudTypeID: 100, isNewlyAdded: false };
                        } else {
                            // This is a truly new case being re-added (Insert)
                            return { ...item, crudTypeID: 300, isNewlyAdded: true };
                        }
                    } else if (item.isNewlyAdded === true) {
                        // Remove item by not including it in the updated data array
                        return null; // This will be filtered out later
                    } else {
                        // Update item for deletion
                        return { ...item, crudTypeID: 200 };
                    }
                }
                return item;
            }).filter(item => item !== null); // Filter out the null value (the removed item)

            caseDataArrayRef.current = updatedData;
            
            console.log('🔍 Modified case array:', {
                caseNumber,
                isRestoring,
                updatedData: updatedData.map(item => ({
                    caseNumber: item.caseNumber,
                    crudTypeID: item.crudTypeID,
                    isNewlyAdded: item.isNewlyAdded
                }))
            });
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "modifyCaseArray", {});
        }
    };

    const RemoveDocFromCase = async (caseNumber) => {
        try {
            const updatedCaseData = resultsCases.filter((item) => item["caseNumber"] !== caseNumber);
            setCaseResults(updatedCaseData);
            // do not remove casenumber from caseDataArrayRef instead crudtype is updated in modifyCaseArray
            debouncedValidation(selectedDocType, updatedCaseData, formValues, isConfidential);
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "RemoveDocFromCase", {});
        }
    };

    // Function to handle clear of case search value
    const handleCaseClear = async () => {
        setIsPageLoading(true);
        try {
            await simulateAsyncDelay();

            setCaseText("");

            const updatedFormValues = {
                ...formValues,
                 caseNumberTxt: "",
                caseNumberSearched: ""
            }
            // Reset isConfidential
            //setIsConfidential(false)
            debouncedValidation(selectedDocType, resultsCases, updatedFormValues);
        }
        catch (error) {
            await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleCaseClear");
        }
        finally { setIsPageLoading(false); }
    };

    const changeCaseNumberIconsDisable = (iconsEnabledValue) => {
        try {
            const updatedCaseNumberIconsEnabled = {
                ...caseNumberIconsDisabled,
                search: iconsEnabledValue.search,
                copy: iconsEnabledValue.copy,
                clear: iconsEnabledValue.clear,
            };
            updateArrayStateValues(setCaseNumberIconsDisabled, updatedCaseNumberIconsEnabled);
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "changeCaseNumberIconsDisable", {});
        }
    };

    const checkCaseNumberRegEx = (caseNumber) => {// RC removed async as its not needed
        try {
            let isValidFormat = true;
            const caseNumberRegexValue = new RegExp(caseNumberRegex.caseRegexValue || "");

            if ((caseNumberRegexValue !== "") && (caseNumber !== "")) {
                if (!caseNumberRegexValue.test(caseNumber)) {
                    caseNumberRef.current.setValidationMessage(caseNumberRegex.caseRegexMessage);
                    changeCaseNumberIconsDisable({ search: true, copy: false, clear: false });
                    setDisableKeyDownOnCaseSearch(true);
                    isValidFormat = false;
                }
            }

            if (isValidFormat) {
                caseNumberRef.current.setValidationMessage("");
                changeCaseNumberIconsDisable({ search: false, copy: false, clear: false });
                setDisableKeyDownOnCaseSearch(false);
            }

            return isValidFormat;
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "checkCaseNumberRegEx", {});
        }
    };

    const handleCaseChange = async (e) => {
        try {
            // RC do not make any async calls before case number 
            // state update to ensure the case number cursor stays 
            // in place while editing
            //const { value } = e.target;
            const valueText = e.target.value;

            checkCaseNumberRegEx(valueText);

            setCaseText(valueText);

            const updatedFormValues = {
                ...formValues,
                 caseNumberTxt: valueText,
                caseNumberSearched: valueText
            }
            // Reset isConfidential
            setIsConfidential(false)
            debouncedValidation("", resultsCases, updatedFormValues);
            
        }
        catch (error) {
            await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleCaseChange");
        }
    };

    const addNewCaseInfo = async (newCaseData, confidential) => {
        try {
            console.log("new case data", newCaseData)
            if (AddDocToNewCase(newCaseData, confidential)) {
                MergeDocToNewCase(newCaseData);

                // 🔍 FIXED: Check if this is a NoCase to WithCase transition
                const isTransitioningFromNoCase = originalCaseList.length === 0 && resultsCases.length === 0;
                const hasDocumentTypeSelected = selectedDocType && selectedDocType !== "";

                // 🔍 FIXED: Only load narrative if document type is selected
                if (hasDocumentTypeSelected) {// RC removed !isTransitioningFromNoCase
                    //Load Narrative
                    const selectedDocTypeText = documentTypes.find((option) => option.documentTypeID === selectedDocType)?.documentTypeName || "";

                    const narrativeText = await loadNarrativeText(caseBatchDocumentID, selectedGroupID, selectedDocType, selectedDocTypeText);
                    if (narrativeForStateRef.current) {
                        narrativeForStateRef.current.setValue(narrativeText);
                    }
                    setNarrativeState(narrativeText);

                    const updatedData = caseDataArrayRef.current.map(item => ({
                        ...item,
                        narrative: narrativeText
                    }));

                    caseDataArrayRef.current = updatedData;
                } else {
                    // 🔍 FIXED: Clear narrative for NoCase transition or when no doc type selected
                    console.log('🔍 Keeping narrative cleared - transitioning from NoCase or no doc type selected');
                    if (narrativeForStateRef.current) {
                        narrativeForStateRef.current.setValue("");
                    }
                    setNarrativeState("");

                    const updatedData = caseDataArrayRef.current.map(item => ({
                        ...item,
                        narrative: ""
                    }));
                
                    // setCaseDataArray(updatedData);
                    caseDataArrayRef.current = updatedData
                }


            }
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "addNewCaseInfo", {});
        }
    };
    
    // Function to process iConnect for a newly added case (similar to NextWorkItem pattern)
    const processIConnectForNewCase = async (caseData, selectedDocTypeText) => {
        try {
            if (stateID !== EnumUtility.StateIDKey.Mississippi.ID) {
                return; // Only process for Mississippi users
            }
            console.log(`ManageKeywords: Processing iConnect for newly added case ${caseData.caseNumber}`);
            
            // Get proper user session data
            const sessionLoginUser = sessionService.getUser();
            const userLoginID = sessionLoginUser ? sessionLoginUser.legacyUserID : -123456;
            const yTracSiteID = sessionLoginUser ? sessionLoginUser.siteID : -123456;
            
            if (!userLoginID || !yTracSiteID) {
                console.error(`ManageKeywords: Missing required user data for iConnect: userLoginID=${userLoginID}, yTracSiteID=${yTracSiteID}`);
                return;
            }
            
            // Format SSN data like NextWorkItem does (remove spaces AND hyphens)
            let regCol = ''; // NCP SSN
            let reqCol = ''; // CP SSN
            
            if (caseData.ncpSSN || caseData.NCPSSN || caseData.ncpssn || caseData.NCP_SSN) {
                regCol = (caseData.ncpSSN || caseData.NCPSSN || caseData.ncpssn || caseData.NCP_SSN || '').replace(/[ -]/g, '').trim();
            }
            if (caseData.cpSSN || caseData.CPSSN || caseData.cpssn || caseData.CP_SSN) {
                reqCol = (caseData.cpSSN || caseData.CPSSN || caseData.cpssn || caseData.CP_SSN || '').replace(/[ -]/g, '').trim();
            }
            
            // Create DocTransferLog entry following NextWorkItem pattern
            const docTransferLog = {
                userLoginID: userLoginID,
                yTracSiteID: yTracSiteID,
                batchID: caseData.batchID || documentData?.batchID || 0,
                batchDocumentID: caseData.batchDocumentID || caseBatchDocumentID || documentData?.batchDocumentID || 0,
                documentName: documentData?.documentName || `ManageKeywords_${caseData.caseNumber}_${new Date().getTime()}`,
                caseNumber: caseData.caseNumber,
                contactRoleID: caseData.contactRoleID || 0,
                stateDocTypeName: caseData.stateDocTypeName || selectedDocTypeText || '',
                stateDocTransferKey: 0,
                stateStatusName: 'PENDING',
                stateResponse: '',
                notes: 'Document processed via ManageKeywords - iConnect transfer initiated (newly added case)',
                regCol: regCol,  // NCP SSN formatted like NextWorkItem
                reqCol: reqCol,  // CP SSN formatted like NextWorkItem
                processCompleteFlag: 0
            };
            
            console.log(`ManageKeywords: Creating DocTransferLog for newly added case ${caseData.caseNumber}`, {
                regCol: regCol ? 'Present' : 'Missing',
                reqCol: reqCol ? 'Present' : 'Missing',
                batchDocumentID: docTransferLog.batchDocumentID,
                userLoginID: userLoginID,
                yTracSiteID: yTracSiteID
            });
            
            // Submit the DocTransferLog entry
            const result = await ApiManager.PostDocTransferLogG4ToApi(docTransferLog);
            
            if (result && result.success) {
                console.log(`ManageKeywords: DocTransferLog created successfully for newly added case ${caseData.caseNumber}`);
                if (result.data) {
                    console.log(`ManageKeywords: Database returned:`, result.data);
                }
            } else {
                console.error(`ManageKeywords: Failed to create DocTransferLog for newly added case ${caseData.caseNumber}:`, result?.error || 'Unknown error');
            }
            
        } catch (error) {
            console.error(`ManageKeywords: Error processing iConnect for newly added case ${caseData.caseNumber}:`, error);
            await LoggerUtility.ManageErrorLogging(error, errorFormName, `iConnect processing for newly added case ${caseData.caseNumber}`);
        }
    };

    // Function to handle Case search
    const handleCaseSearch = async () => {
        setIsPageLoading(true);
        try {
            await simulateAsyncDelay();
            const isValidCaseFormat = await checkCaseNumberRegEx(caseText);
            if (isValidCaseFormat) {

                const isDuplicate = resultsCases.some(c => c.caseNumber === caseText);
                if (isDuplicate) {
                    console.log('🔍 Case already on the list, ignoring search.');
                    // Optionally, show a message to the user
                    showAlertMessage('info', `Case ${caseText} is already on the list.`);
                    return; // Exit the function early
                }

                const params = {
                    caseNumber: caseText,
                    apiMainframeUrl: configLibrary.apiMainframeUrl,
                    masterServerDBSchema
                };
                
                const newCaseData = await ApiManager.GetCaseDataFromApiByID(params);

                // Fix: Handle the new object structure instead of array
                if (newCaseData && newCaseData.caseInfo && newCaseData.statusCode === EnumUtility.CaseSearchResultCode.CaseFound.ID) {
                    const caseInfoData = newCaseData.caseInfo;
                    
                    let confidential = false;
                    if (stateID === EnumUtility.StateIDKey.Mississippi.ID) {
                        const updatedCaseData = await CaseWorkflowService.buildYTracCaseData(caseInfoData, stateID);
                        confidential = CaseWorkflowService.checkConfidential(updatedCaseData);
                        setIsConfidential(confidential);
                    }
                    
                    // Possibly show warning if case is confidential. Depends on user METSS permissions.
                    if (confidential) {
                        const messageContent = await SharedService.getMessageContent(
                            ClientType.MessageContent.N_UI4024_Confidential_Case_Warning
                        );
                        const dialogContent = {
                            NotificationType: "warning",
                            Title: "Warning",
                            Message: messageContent?.messageText || "This case has been marked confidential"
                        };
                        handleNotificationDialog(dialogContent);
                    }

                    await addNewCaseInfo(caseInfoData, confidential);
                    showAlertMessage('success', 'Case ID found!');
                    
                }
                else {
                    if (siteSettingDetail.CaseNumberInsertAllow === "1") {
                        const updatedCaseData = { ...emptyCaseData, caseNumber: caseText };
                        await addNewCaseInfo(updatedCaseData);
                    }
                    else {
                        let errorMessage = {};
                        if (newCaseData.statusCode === EnumUtility.CaseSearchResultCode.CaseNotFound.ID) {
                            const loginUserInfo = sessionService.getUser();
                            const userStateID = loginUserInfo ? loginUserInfo.stateID : -1;
                            errorMessage = EnumUtility.getCaseNumberNotFoundError(userStateID);
                            // Only show error if user is in MS/KS/WY, otherwise skip showing error
                            if (!errorMessage) {
                                return; // Exit without showing error for other states
                            }
                        }
                        else if ([ EnumUtility.CaseSearchResultCode.MFSessionDisconnectError.ID,
                            EnumUtility.CaseSearchResultCode.ScraperDisconnectedError.ID].includes(newCaseData.statusCode )) {
                            let messageContentID = null;
                            if (newCaseData.statusCode === EnumUtility.CaseSearchResultCode.MFSessionDisconnectError.ID){
                                messageContentID = ClientType.MessageContent.E_MF7049_Lost_Connection_To_State_System
                            }
                            else{
                                messageContentID = ClientType.MessageContent.E_UI7056_Scrapper_Connection_Status;
                            }
                            const messageContent = await SharedService.getMessageContent(messageContentID)
                            errorMessage = { NotificationType: 'error', Message: messageContent?.messageText?.replace(/\\n/g, "\u00A0\n") ?? "" }
                            }
                            else if (newCaseData.statusCode === EnumUtility.CaseSearchResultCode.Enter_A_Function_Error.ID) {
                                const messageContent = await SharedService.getMessageContent(ClientType.MessageContent.N_UI7058_ENTER_A_FUNCTION_Error_Occured);
                                errorMessage = { NotificationType: 'error', Message: messageContent?.messageText?.replace(/\\n/g, "\u00A0\n") ?? "" };
                            }
                            else {
                                errorMessage = { ID: 321, Name: "NetworkError", NotificationType: EnumUtility.NotificationType.Warning, Title: "Warning", Message: newCaseData.errorMessage };
                            }
                        const dialogContent = {
                            ...errorMessage,
                            dynamicMessageContent: [caseText],
                        };
                        handleNotificationDialog(dialogContent);
                        //showAlertMessage('warning', 'No Case ID found! Please enter a valid Case ID');
                    }
                }
            }
            else {
                const dialogContent = {
                    ...EnumUtility.NotificationAlerts.CaseNumberFormatInvalid,
                    dynamicMessageContent: [caseText, caseNumberRegex.caseRegexMessage],
                };
                handleNotificationDialog(dialogContent);
            }
        }
        catch (error) {
            const errorType = error?.Details?.type;
            if ([ EnumUtility.CaseSearchResultCode.MFSessionDisconnectError.Name,
                EnumUtility.CaseSearchResultCode.ScraperDisconnectedError.Name,
                EnumUtility.CaseSearchResultCode.Enter_A_Function_Error.Name].includes(errorType)) {
                let messageContentID = null;
                if (errorType === EnumUtility.CaseSearchResultCode.MFSessionDisconnectError.Name){
                    messageContentID = ClientType.MessageContent.E_MF7049_Lost_Connection_To_State_System;
                }
                else if (errorType === EnumUtility.CaseSearchResultCode.Enter_A_Function_Error.Name){
                    messageContentID = ClientType.MessageContent.N_UI7058_ENTER_A_FUNCTION_Error_Occured;
                }
                else{
                    messageContentID = ClientType.MessageContent.E_UI7056_Scrapper_Connection_Status;
                }
                const messageContent = await SharedService.getMessageContent(messageContentID)
                handleNotificationDialog({ NotificationType: 'error', Message: messageContent?.messageText });
            }
            await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleCaseSearch");
        }
        finally { setIsPageLoading(false); }
    };

    const handleConfirmationDialog = async (caseNumber) => {
        try {
            setConfirmationDialogContent(EnumUtility.ConfirmDialogType.RemoveDocumentFromCase);
            setRemoveCaseNumber(caseNumber);
            handleOpenConfirmDialog();
        }
        catch (error) {
            await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleConfirmationDialog");
        }
    };

    const handleCancelConfirmationDialog = async () => {
        try {
            setConfirmationDialogContent(EnumUtility.ConfirmDialogType.NewDocumentCancel);
            handleOpenConfirmDialog();
        }
        catch (error) {
            await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleConfirmationDialog");
        }
    };

    const handleOpenConfirmDialog = () => {
        try {
            //setRemoveCaseNumber(caseNumber);
            setConfirmDialogOpen(true);
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "handleOpenConfirmDialog", {});
        }
    };

    const handleCloseConfirmDialog = async () => {
        try {
            setConfirmDialogOpen(false);
            setRemoveCaseNumber(null);
        }
        catch (error) {
            await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleCloseConfirmDialog");
        }
    };

    const handleConfirmAction = async (dialogID) => {
        try {
            RemoveDocFromCase(removeCaseNumber);
            modifyCaseArray(removeCaseNumber);
            setConfirmDialogOpen(false);
            setRemoveCaseNumber(null);
        }
        catch (error) {
            await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleConfirmAction");
        }
    };

    const handleDialogConfirmAction = async (dialogContentID) => {
    try {
      switch (dialogContentID) {

        case EnumUtility.ConfirmDialogType.NewDocumentCancel.ID:
           setter({
                showManageKeyword: false,
                showMessage: false,
                dialogContent: ""
            });
          break;

        case EnumUtility.ConfirmDialogType.RemoveDocumentFromCase.ID:
          handleConfirmAction();
          break;

        default:
          break;
      }

      setConfirmDialogOpen(false);
    }
    catch (error) {
      await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleDialogConfirmAction");
    }
  };

  
    const handleNotesChange = async (event) => {
        try {
            const { name, value } = event.target;
            setNotesForAction(value);
            const updatedFormValues = { ...formValues, [name]: value };
            // Update form values
            setFormValues(updatedFormValues);
            // 🔍 FIXED: Pass the CURRENT selectedDocType and resultsCases to validation.
            debouncedValidation(selectedDocType, resultsCases, updatedFormValues, isConfidential);
        }
        catch (error) {
            await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleNotesChange");
        }
    };

    const handleNarrativeChange = async (event) => {
        try {
            const { name, value } = event.target;
            setNarrativeState(value);
            const updatedFormValues = { ...formValues, [name]: value };
            setFormValues(updatedFormValues);
        }
        catch (error) {
            await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleNarrativeChange");
        }
    };

    const handleNotificationDialog = (dialogContent) => {
        try {
            setNotificationDialogContent(dialogContent);
            handleOpenNotificationDialog();
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "handleNotificationDialog", {});
        }
    };

    const showNarrativeUpdateFailure = (apiErrorMessage) => {
        handleNotificationDialog({
            NotificationType: EnumUtility.NotificationType.Error,
            Title: 'Error',
            Message: apiErrorMessage || 'Failed to update narrative. Check the scraper app and the mainframe.'
        });
    };

    const handleOpenNotificationDialog = () => {
        try {
            setNotificationDialogOpen(true);
        }
        catch (error) {
            throw LoggerUtility.ProcessErrorData(error, "handleOpenNotificationDialog", {});
        }
    };

    const handleCloseNotificationDialog = useCallback(async () => {
        try {
            setNotificationDialogOpen(false);
        }
        catch (error) {
            await LoggerUtility.ManageErrorLogging(error, errorFormName, "handleCloseNotificationDialog");
        }
    }, []);

    // const getDynamicDocGroupList = useCallback(() => {
    //     // 🔍 SIMPLIFIED LOGIC
    //     if (resultsCases.length > 0) {
    //         // When cases exist, always show case-compatible groups plus "All"
    //         const caseCompatibleGroups = allDocumentGroups.filter(group => 
    //             group.caseNoCaseFlag === 2 || group.caseNoCaseFlag === 3
    //         );
            
    //         const allOption = { 
    //             documentGroupTypeID: 0, 
    //             documentGroupTypeName: "All", 
    //             documentTypes: [], // This can be empty as it's just for the label
    //             caseNoCaseFlag: 2, // Match the flag for consistency
    //             displayOrder: -1 
    //         };
            
    //         const result = [allOption, ...caseCompatibleGroups];
    //         console.log('🔍 Returning WithCase groups:', result.map(g => g.documentGroupTypeName));
    //         return result;
    //     } else {
    //         // When no cases exist, show no-case compatible groups
    //         const noCaseCompatibleGroups = allDocumentGroups.filter(group => 
    //             group.caseNoCaseFlag === 1 || group.caseNoCaseFlag === 3
    //         );
    //         console.log('🔍 Returning NoCase groups:', noCaseCompatibleGroups.map(g => g.documentGroupTypeName));
    //         return noCaseCompatibleGroups;
    //     }
    // }, [resultsCases.length, allDocumentGroups]); // 🔍 REMOVED unnecessary dependencies

    useEffect(() => {
        if (resultsCases.length > 0) {
            // When cases exist, always show case-compatible groups plus "All"
            const caseCompatibleGroups = allDocumentGroups.filter(group => 
                group.caseNoCaseFlag === 2 || group.caseNoCaseFlag === 3
            );
            
            const allOption = { 
                documentGroupTypeID: 0, 
                documentGroupTypeName: "All", 
                documentTypes: [],
                caseNoCaseFlag: 2,
                displayOrder: -1 
            };
            
            setDynamicDocGroupList([allOption, ...caseCompatibleGroups]);
        } else {
            // When no cases exist, show no-case compatible groups
            const noCaseCompatibleGroups = allDocumentGroups.filter(group => 
                group.caseNoCaseFlag === 1 || group.caseNoCaseFlag === 3
            );
            setDynamicDocGroupList(noCaseCompatibleGroups);
        }
    }, [resultsCases.length, allDocumentGroups]);

    return (
        <div >
            <div className="pane-main-container-v01" >
                {/* Header Section */}
                <header className="form-header">
                    <div className='d-flex justify-content-between align-items-center p-2'>
                        <p className='manage_key_txt'>Manage Keywords</p>
                        <IconButton onClick={handleCloseManageKeywordsClick}
                            sx={{
                                alignSelf: 'center'
                            }}
                        >
                            <Close />
                        </IconButton>
                    </div>
                </header>
                {/* Form Section */}
                <div className="form-content">
                    <div className='p-2'>
                        <Box display="flex" flexDirection="column" gap={2} mt={2}>
                            {!(formFieldAccess.caseNumberSearchTxt?.hide ?? false) && (

                                <TextFieldWithIconButton
                                    ref={caseNumberRef}
                                    nameForField="caseNumberSearchTxt"
                                    onIconButtonClick={handleCaseSearch}
                                    onClearButtonClick={handleCaseClear}
                                    onTextChange={handleCaseChange}
                                    valueForField={caseText}
                                    labelForField="New Case Search"
                                    iconsDisabled={caseNumberIconsDisabled}
                                    iconsTooltipText={{ search: 'Search Case', copy: 'Copy Case', clear: 'Clear Case' }}
                                    isDisabled={!(formFieldAccess.caseNumberSearchTxt?.write ?? true)}
                                    disableKeyDown={disableKeyDownOnCaseSearch}
                                />
                            )}

                            {!(formFieldAccess.docGroupSelect?.hide ?? false) && (
                                <FormControl fullWidth>
                                    <Autocomplete
                                        options={dynamicDocGroupList}
                                        getOptionLabel={(option) => option.documentGroupTypeName || ""}
                                        isOptionEqualToValue={(option, value) => option.documentGroupTypeID === value?.documentGroupTypeID}
                                        id="docGroupSelect"
                                        name="docGroupSelect"
                                        value={dynamicDocGroupList.find(opt => opt.documentGroupTypeID === selectedGroupID) || null}
                                        IconComponent={ArrowDropDown}
                                        label="Document Group"
                                        noOptionsText="Document Group not found"
                                        displayEmpty
                                        disabled={!(formFieldAccess.docGroupSelect?.write ?? true) || resultsCases.length === 0}
                                        onChange={(event, newValue, reason) => {
                                            console.log('🔍 Document Group onChange:', {
                                                newValue: newValue ? `${newValue.documentGroupTypeID}:${newValue.documentGroupTypeName}` : 'null',
                                                reason: reason
                                            });
                                            
                                            const modifiedEvent = {
                                                target: {
                                                    name: "docGroupSelect",
                                                    childName: "docTypeSelect"
                                                },
                                            };
                                            handleGroupChange(modifiedEvent, newValue, reason);
                                        }}
                                        renderInput={(params) => (
                                            <TextField {...params}
                                                label="Document Group"
                                                variant="outlined"
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                                helperText={resultsCases.length === 0 ? "Add at least one case to enable" : ""}
                                            />)}
                                    />
                                </FormControl>
                            )}

                            {!(formFieldAccess.docTypeSelect?.hide ?? false) && (
                                <FormControl fullWidth>
                                    <Autocomplete
                                        options={documentTypes}
                                        getOptionLabel={(option) => option.documentTypeName || ""}
                                        isOptionEqualToValue={(option, value) => option.documentTypeID === value?.documentTypeID}

                                        id="docTypeSelect"
                                        name="docTypeSelect"
                                        value={documentTypes.find((opt) => opt.documentTypeID === selectedDocType) || null}
                                        IconComponent={ArrowDropDown}
                                        label="Document Type"
                                        noOptionsText="Document Type not found"
                                        displayEmpty

                                        disabled={!(formFieldAccess.docTypeSelect?.write ?? true) || resultsCases.length === 0} // 🔍 ADDED: Disable when no cases

                                        onChange={(event, newValue, reason) => {
                                            const modifiedEvent = {
                                                target: {
                                                    name: "docTypeSelect",
                                                    parentName: "docGroupSelect"
                                                },
                                            };
                                            handleDocType(modifiedEvent, newValue, reason);
                                        }}

                                        renderInput={(params) => (
                                            <TextField {...params}
                                                label="Document Type"
                                                variant="outlined"
                                                InputLabelProps={{
                                                    shrink: true,
                                                }}
                                                // 🔍 ADDED: Show helper text when disabled due to no cases
                                                helperText={resultsCases.length === 0 ? "Add at least one case to enable" : ""}
                                            />)}
                                    >
                                    </Autocomplete>
                                </FormControl>
                            )}

                            {!(formFieldAccess.noteForActionTxt?.hide ?? false) && (
                                <div className="col-12 mb-3 align-items-center justify-center">
                                    <TextAreaWithCount
                                        ref={noteForActionRef}
                                        id="noteForActionTxt"
                                        nameForField="noteForActionTxt"
                                        labelValue="Y-Trac Note for Action Taken"
                                        initialValue={notesForAction}
                                        maxLength={notesCharLimit}
                                        placeholder=""
                                        onTextChange={handleNotesChange}
                                        isDisabled={!(formFieldAccess.noteForActionTxt?.write ?? true)}
                                        requiredFieldLabel={requiredFieldLabels.noteForActionTxt}
                                    />
                                </div>
                            )}

                            {!(formFieldAccess.narrativeForStateTxt?.hide ?? false) && (
                                <div className="col-12 mb-3 align-items-center justify-center">
                                    <TextAreaWithCount
                                        ref={narrativeForStateRef}
                                        id="narrativeForStateTxt"
                                        nameForField="narrativeForStateTxt"
                                        labelValue="Narrative for State System"
                                        initialValue={narrativeState}
                                        maxLength={narrativeCharLimit}
                                        placeholder=""
                                        onTextChange={handleNarrativeChange}
                                        isDisabled={!(formFieldAccess.narrativeForStateTxt?.write ?? true)}
                                    />
                                </div>

                            )}
                        </Box>
                        {!(formFieldAccess.casesListDgrd?.hide ?? false) && (
                            resultsCases.length > 0 ? (
                                <div style={{ width: "100%" }}>
                                    <div className="text-wrapper-header" style={{ padding: '30px 5px 10px' }}>List of Cases</div>
                                    <div style={{ marginTop: '8px', display: "flex", flexDirection: "column", height: "100%", width: "100%", overflowX: "auto" }}>
                                        <DataGridPremium
                                            id="casesListDgrd"
                                            name="casesListDgrd"
                                            rows={resultsCases}
                                            columns={columns}
                                            rowsPerPageOptions={[5]}
                                            pagination
                                            pageSizeOptions={[5, 10, 15, 20, 25]}
                                            paginationModel={paginationModel} // Controlled pagination
                                            onPaginationModelChange={setPaginationModel} // Handle page changes
                                            disableSelectionOnClick
                                            disableRowSelectionOnClick
                                            getRowId={(row) => `${row.caseNumber}-${row.rowIndex}`}
                                            sx={{
                                                ".MuiTablePagination-displayedRows, .MuiTablePagination-selectLabel": {
                                                    "margin-top": "1em",
                                                    "margin-bottom": "1em"
                                                },
                                                "& .MuiDataGrid-cell": {
                                                    cursor: "default", // Default cursor for all cells
                                                },
                                                "& .MuiDataGrid-cell[data-field='caseNumber']": {
                                                    cursor: "pointer", // Pointer only for the 'action' column
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                            ) : <div className="text-wrapper-subtitle">No case(s) found for the document!</div>

                        )}
                    </div>
                </div>
                {/* Footer Section */}
                <footer className="form-footer">
                    <div className="d-flex justify-content-between align-items-center mb-2 form-actions">
                        {!(formFieldAccess.cancelBtn?.hide ?? false) && (
                            <div className='leftDiv'>
                                <Button id="cancelBtn" name="cancelBtn" color="ywButtonWhite" variant='outlined' onClick={handleCloseManageKeywordsClick} disabled={!(formFieldAccess.cancelBtn?.write ?? true)}>cancel</Button>
                            </div>
                        )}
                        {!(formFieldAccess.saveBtn?.hide ?? false) && (
                            <div className='rightDiv'>
                                <Button id="saveBtn" name="saveBtn" color="ywButtonBlack" variant='contained' disabled={!isSaveEnabled} onClick={handleSaveClick}>Save</Button>
                            </div>
                        )}
                    </div>
                </footer>

            </div>
            <div >
                {/* Include the loader component */}
                <PageLoader isPageLoading={isPageLoading} />

                {/* Include the Confirmation Dialog component */}
                <ConfirmationDialog
                    openConfirmation={confirmDialogOpen}
                    dialogContent={confirmationDialogContent}
                    onCloseConfirmation={handleCloseConfirmDialog}
                    onConfirmDialog={(param) => handleDialogConfirmAction(param)}
                />

                {/* Include the Notification Dialog component */}
                <NotificationDialog
                    openNotification={notificationDialogOpen}
                    dialogContent={notificationDialogContent}
                    onCloseNotification={handleCloseNotificationDialog}
                    isAutoClose={false}
                />
            </div>
        </div>
    )
}

export default ManageKeywords;
